<template>
  <div v-if="list.length" class="finding-field">
    <div v-if="label" class="finding-field-label">{{ label }}</div>

    <div v-if="kind === 'chips'" class="chip-row">
      <span
        v-for="(item, i) in list"
        :key="i"
        :class="['chip', `chip-${tone}`, { 'chip-mono': mono }]"
      >{{ displayValue(item) }}</span>
    </div>

    <div v-else-if="kind === 'urls'" class="url-stack">
      <div v-for="(item, i) in list" :key="i" :class="{ 'url-box': boxed }">
        <a v-if="href(item)" :href="href(item)" target="_blank" rel="noopener" class="finding-url">{{ labelFor(item) }}</a>
        <span v-else>{{ labelFor(item) }}</span>
      </div>
    </div>

    <ul v-else-if="kind === 'bullets'" class="finding-bullets">
      <li v-for="(item, i) in list" :key="i">{{ displayValue(item) }}</li>
    </ul>

    <div v-else-if="kind === 'objects'">
      <div v-for="(item, i) in list" :key="i" class="object-card">
        <div v-for="[key, value] in objectEntries(item)" :key="key" class="object-row">
          <span class="object-key">{{ humanizeKey(key) }}</span>
          <a v-if="isHttpUrl(value)" :href="value" target="_blank" rel="noopener" class="finding-url">{{ value }}</a>
          <span v-else>{{ displayValue(value) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { asList, humanizeKey, isHttpUrl } from '@/utils/diagnosisFindings'

export default {
  name: 'DiagnosisFieldBlock',
  props: {
    kind: {
      type: String,
      default: 'chips'
    },
    label: {
      type: String,
      default: ''
    },
    items: {
      default: () => []
    },
    tone: {
      type: String,
      default: 'neutral'
    },
    mono: {
      type: Boolean,
      default: false
    },
    boxed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    list() {
      return asList(this.items)
    }
  },
  methods: {
    humanizeKey,
    isHttpUrl,
    displayValue(item) {
      if (Array.isArray(item)) return item.join(', ')
      if (typeof item === 'boolean') return item ? 'yes' : 'no'
      if (item && typeof item === 'object') return item.title || item.url || item.name || JSON.stringify(item)
      return String(item)
    },
    href(item) {
      if (typeof item === 'string') return isHttpUrl(item) ? item.trim() : null
      return item?.url || null
    },
    labelFor(item) {
      if (typeof item === 'string') return item
      return item?.title || item?.url || JSON.stringify(item)
    },
    objectEntries(item) {
      if (!item || typeof item !== 'object') return [['value', item]]
      return Object.entries(item).filter(([, value]) => value != null && value !== '' && !(Array.isArray(value) && !value.length))
    }
  }
}
</script>

<style scoped>
.finding-field-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-ink-800);
  margin-bottom: 6px;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.chip-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
}

.chip-info {
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
}

.chip-positive {
  background: var(--brand-teal-100);
  color: #127a6a;
}

.chip-negative {
  background: var(--brand-weak-100);
  color: #9b2c2c;
}

.chip-warn {
  background: var(--brand-amber-100);
  color: #92610a;
}

.chip-neutral {
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

.url-box,
.object-card {
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 12px 14px;
}

.finding-url {
  color: var(--brand-ocean-600);
  font-size: 12.5px;
  word-break: break-all;
}

.url-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.finding-bullets {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: var(--brand-ink-800);
  line-height: 1.5;
}

.object-row {
  display: flex;
  gap: 10px;
  font-size: 12.5px;
  margin-bottom: 4px;
}

.object-key {
  font-weight: 600;
  color: var(--brand-ink-800);
  min-width: 120px;
  text-transform: capitalize;
}
</style>
